export const HELP = {
  buttons: {
    load: "Load the selected scene from the server. Unsaved editor changes are replaced.",
    "scene-new":"Create a new server-side scene directory and initial scene.json.",
    "scene-duplicate":"Duplicate scene configuration and responses without copying large media assets.",
    "scene-rename":"Safely rename this scene directory and update its JSON id. Incoming references are listed first.",
    "scene-delete":"Delete this scene only after exact-id confirmation and a server-side backup.",
    "scene-settings":"Clear object selection and open the organized scene-level settings.",
    save: "Save this scene to the server. Revision checking prevents silently overwriting newer work.",
    undo: "Undo the most recent scene edit. Shortcut: Ctrl/Cmd+Z.",
    redo: "Redo the most recently undone edit. Shortcut: Ctrl/Cmd+Shift+Z.",
    duplicate: "Duplicate the selected object and offset the copy slightly. Shortcut: Ctrl/Cmd+D.",
    delete: "Delete the selected object from the scene. Shortcut: Delete.",
    "import-json": "Import a version 1 scene JSON file into the editor. It is not saved automatically.",
    "export-json": "Download the current scene configuration as a formatted JSON file.",
    preview: "Start the Babylon.js preview using the current unsaved scene data.",
    "stop-preview": "Stop preview mode and return to editing.",
    restore: "Restore the newer local recovery copy kept by this browser.",
    "dismiss-recovery": "Keep the server version and hide the local recovery notice."
    ,interactionAdd: "Add a new interaction using the selected verb preset."
    ,interactionDelete: "Remove this interaction from the hotspot."
    ,interactionDuplicate: "Duplicate this interaction and place the copy directly after it."
    ,interactionUp: "Move this interaction one position earlier in the context menu."
    ,interactionDown: "Move this interaction one position later in the context menu."
    ,responseEdit: "Open the response referenced by this action in the scene response editor."
    ,"response-add": "Add a new English response to this scene."
    ,"response-duplicate": "Duplicate the selected response under a new id."
    ,"response-delete": "Delete an unused response. Referenced responses are protected."
    ,actionStepAdd: "Append an action to this sequence."
    ,actionStepDelete: "Remove this action from the sequence."
    ,actionStepUp: "Move this action one step earlier."
    ,actionStepDown: "Move this action one step later."
    ,"background-play": "Play or pause the single active background video for preview and debugging."
    ,"weather-restart":"Restart the authored weather state machine from its initial phase without changing scene JSON."
    ,"weather-force-dry":"Temporarily fade the editor preview to dry weather without changing scene JSON."
    ,"weather-force-rain":"Temporarily force rain in the editor preview without changing scene JSON."
    ,"weather-resume":"Discard the preview override and resume the authored weather mode."
  },
  tools: {
    select: "Select objects and drag polygons or anchors. Hover a polygon edge to insert a new anchor.",
    walkable: "Draw a Walkable polygon: an area where the character is allowed to move.",
    hotspot: "Draw a Hotspot polygon: an interactive region that can run an action after walking to it.",
    exit: "Draw an Exit polygon: a region that can move the player to another scene or spawn.",
    occluder: "Draw an Occluder polygon: a foreground region intended to visually cover the character.",
    "lighting-zone": "Draw a Lighting zone polygon that changes only the 3D character's lighting.",
    "weather-exclusion": "Draw a Weather exclusion polygon that masks rain or splashes beneath cover.",
    "animated-prop": "Create a local animated scene patch. The Lift preset provides doors, boarding, travel and a title card.",
    spawn: "Place a Spawn point: a possible starting position and facing direction for the character.",
    walkTo: "Set Walk-to: the position the character walks to before a hotspot or exit action runs.",
    faceTo: "Set Face-to: the point the character turns toward after reaching Walk-to.",
    nearLine: "Set the Near line: characters at this image height use the configured near scale.",
    farLine: "Set the Far line: characters at this image height use the configured far scale.",
    pan: "Pan the artwork without changing scene coordinates.",
    zoom: "Click to zoom in; Shift-click to zoom out. The mouse wheel also zooms."
  },
  fields: {
    scene: "Choose which server-side scene to load into the editor.",
    sceneSettings:"Choose a scene settings section. The last opened section is remembered for this browser session.",
    previewDebug: "Show navigation polygons, perspective lines, target, position and scale during preview.",
    id: "Unique identifier used to reference this scene object from JSON and actions.",
    type: "Object category. Type is fixed after creation because each type belongs to a different JSON collection.",
    enabled: "Controls whether this object participates in navigation or interaction at runtime.",
    notes: "Free-form editor notes. Notes do not affect runtime behaviour.",
    label: "Human-readable name shown in the viewport and available to the runtime.",
    cost: "Relative movement cost reserved for future pathfinding. Normal terrain uses 1.",
    facing: "Initial horizontal direction of the character at this spawn.",
    default: "Use this spawn when the scene opens without an explicit target spawn.",
    cursor: "Mouse cursor shown while this hotspot is hovered.",
    verb: "Short interaction verb presented for this hotspot, such as Use or Look at.",
    "action.type": "Action type determines what happens after Walk-to and Face-to complete.",
    "action.event": "Custom event name dispatched by a custom-event action.",
    "action.targetScene": "Scene opened by a scene-transition action.",
    "action.targetSpawn": "Spawn used when entering the selected target scene.",
    targetScene: "Scene opened when this exit is activated.",
    targetSpawn: "Spawn used when entering the target scene.",
    transition: "Transition controls the visual scene change: fade, cut or crossfade.",
    layer: "Occluder layer that determines where this foreground region is conceptually rendered.",
    depthOrder: "Ordering value for overlapping occluders; higher values can be rendered later.",
    mode: "Always-front covers the character whenever polygons overlap. Depth-controlled also compares the character Y position.",
    depthY: "Normalized Y threshold used by depth-controlled occlusion.",
    foreground: "Optional foreground image filename associated with this occluder.",
    lightingEnabled:"Enable authored character lighting for this scene.",transitionSeconds:"Seconds used to blend smoothly to a newly sampled lighting result.",lightColor:"Diffuse fill color applied only to character meshes.",lightIntensity:"Character fill-light intensity from 0 to 5.",color:"Diffuse fill color applied only to character meshes.",intensity:"Character fill-light intensity from 0 to 5.",rimColor:"Color of the character-only rim light.",rimIntensity:"Character rim-light intensity from 0 to 5.",exposure:"Character-local brightness multiplier; it does not alter the artwork or video.",characterTint:"Subtle tint blended from each material's original color.",tintStrength:"Tint blend amount from 0 (original material) to 1 (full tint).",priority:"Higher-priority overlapping lighting zones win; equal priorities blend.",softness:"Normalized inward feather distance at the polygon edge; 0 creates a hard boundary.",
    nearY: "Normalized image Y-coordinate of the Near perspective line.",
    farY: "Normalized image Y-coordinate of the Far perspective line.",
    nearScale: "Character scale used at or below the Near line.",
    farScale: "Character scale used at or above the Far line."
    ,skipSingleInteractionMenu: "When enabled, a hotspot with exactly one enabled interaction executes it immediately without opening a menu."
    ,defaultInteraction: "Preferred interaction for systems that need one default choice."
    ,interactionPreset: "Preset used when adding an interaction; fields remain editable afterwards."
    ,"interaction.id": "Unique identifier for this interaction within the hotspot."
    ,"interaction.enabled": "Disabled interactions are hidden from the runtime context menu."
    ,"interaction.verb": "Semantic interaction verb used by runtime logic and UI."
    ,"interaction.label": "Text displayed for this interaction in the context menu."
    ,"interaction.cursor": "Cursor associated with this interaction; hotspot hover itself uses a generic context-menu cursor."
    ,"interaction.requiresWalkTo": "Walk to the effective Walk-to point before performing this interaction."
    ,"interaction.requiresFaceTo": "Turn toward the effective Face-to point before performing this interaction."
    ,"interaction.walkTo.enabled": "Use an interaction-specific Walk-to point instead of the hotspot default."
    ,"interaction.walkTo.x": "Normalized X-coordinate of the Walk-to override."
    ,"interaction.walkTo.y": "Normalized Y-coordinate of the Walk-to override."
    ,"interaction.faceTo.enabled": "Use an interaction-specific Face-to point instead of the hotspot default."
    ,"interaction.faceTo.x": "Normalized X-coordinate of the Face-to override."
    ,"interaction.faceTo.y": "Normalized Y-coordinate of the Face-to override."
    ,"interaction.action.type": "Action performed after optional walking and facing. Walk-only stops after arrival."
    ,"interaction.action.event": "Custom event dispatched by this interaction."
    ,"interaction.action.targetScene": "Scene opened by this interaction."
    ,"interaction.action.targetSpawn": "Spawn used when entering the interaction target scene."
    ,"interaction.action.responseId": "Response id loaded from the scene's localized responses file."
    ,"interaction.action.key": "Game-state key read or written by this action."
    ,"interaction.action.value": "JSON value used by a state or conditional action."
    ,"interaction.action.operator": "Comparison used to choose the conditional Then or Else action."
    ,"response.text": "Primary protagonist line shown the first time this response runs."
    ,"response.id": "Stable localized response id referenced by hotspot actions."
    ,"response.speaker": "Optional speaker name shown above the protagonist line."
    ,"response.repeatText": "Shorter line used after this response id has already been seen."
    ,"response.blocking": "Pauses normal input while visible. An interruptible blocking response can still be replaced by explicit new player input."
    ,"response.interruptible": "Allow a new valid walk or hotspot command to close this response and cancel its remaining action sequence."
    ,"response.duration": "Auto always ends: 900 ms plus 45 ms per normalized text character, clamped between 1.8 and 9 seconds. Or enter custom milliseconds."
    ,"response.once": "After the first display, skip this response unless repeat text is provided."
    ,responseFilter: "Filter this scene's response ids and English text."
    ,responseList: "Select a response to edit and inspect its interaction references."
    ,mediaType: "Choose a static image or one shared video as the living scene background."
    ,mediaBackground: "Poster and fallback image filename under scenes/{scene-id}/."
    ,mediaVideo: "MP4 or WebM filename under scenes/{scene-id}/; paths and external URLs are not allowed."
    ,mediaPoster: "Image shown while video loads, is disabled, or cannot play."
    ,mediaAutoplay: "Try muted playback when the scene opens; blocked playback waits for the first user interaction."
    ,mediaLoop: "Restart the background video automatically when it reaches the end."
    ,mediaMuted: "Keep background video silent; muted video is also eligible for browser autoplay."
    ,mediaPlaysInline: "Keep playback embedded in the scene instead of opening a native fullscreen player."
    ,mediaPlaybackRate: "Video playback multiplier from 0.1 to 4. Changes apply after the media reloads."
    ,mediaFallback: "Use the poster/image when video loading, decoding, autoplay, or browser support fails."
    ,weatherEnabled:"Weather is opt-in. Disabled scenes remain completely dry and create no weather canvases."
    ,weatherType:"Rain is the only weather type in this version."
    ,weatherMode:"Dry disables rain; Always rain fades in once; Random rain cycles using authored ranges; State controlled follows GameState."
    ,weatherIntensity:"Maximum rain density and opacity, from 0 to 1."
    ,weatherVariation:"Stable per-drop brightness variation, from 0 to 1."
    ,weatherWind:"Normalized screen direction and speed. Negative X slants rain left; positive Y falls downward."
    ,weatherFade:"Seconds used for a smooth rain fade without interrupting gameplay."
    ,weatherRandom:"Minimum and maximum durations are sampled once per phase, never every frame."
    ,weatherSeed:"Scene visit changes per entry, session remains stable in this tab, and fixed is deterministic for tests."
    ,weatherLayers:"Choose rain behind and/or in front of the character and distribute density with the layer shares."
    ,weatherSplashes:"Small impacts sampled only inside enabled walkable polygons; disabled automatically on Low quality."
    ,weatherQuality:"Auto selects a bounded preset and respects reduced motion; Low omits foreground rain and splashes."
    ,weatherState:"State-controlled rain is active when this GameState key equals the configured JSON value."
    ,affects:"Select which rain layers this authored exclusion polygon masks."
    ,"affects.background-rain":"Mask the finer rain layer rendered behind the Babylon character."
    ,"affects.foreground-rain":"Mask the larger rain streaks rendered above character occlusion."
    ,"affects.splashes":"Prevent walkable-platform splash impacts inside this polygon."
  },
  objects: {
    walkable: "Defines ground where the character may walk.",
    hotspot: "Interactive region that can perform an action.",
    exit: "Region that can transition to another scene.",
    occluder: "Foreground region intended to cover the character.",
    "lighting-zone":"Authored polygon that blends character fill, rim and material tint.",
    "weather-exclusion":"Authored polygon masking selected rain layers and/or walkable-area splashes.",
    spawn: "Possible starting position for the character.",
    walkTo: "Destination reached before an interaction runs.",
    faceTo: "Point the character faces before an interaction runs.",
    near: "Near perspective line controlling character scale.",
    far: "Far perspective line controlling character scale.",
    anchor: "Polygon anchor. Drag to reshape the polygon.",
    insert: "Insert anchor. Click and drag to add a new polygon corner."
  }
};

const firstSentence = text => text.split(/[.\n]/)[0];

export function objectHelp(object) {
  const lines = [`${object.type} · ${object.id}`];
  if (object.label) lines.push(object.label);
  lines.push(HELP.objects[object.type] || "Scene object.");
  return lines.join("\n");
}

export function anchorHelp(object, index) {
  return `anchor · ${object.id} · point ${index + 1}\n${object.label ? `${object.label}\n` : ""}${HELP.objects.anchor}`;
}

export function pointHelp(type, owner = null) {
  const id = owner?.id || type;
  return `${type} · ${id}\n${owner?.label ? `${owner.label}\n` : ""}${HELP.objects[type] || "Scene point."}`;
}

export function initTooltips(root = document) {
  const tooltip = root.querySelector("#context-tooltip");
  let active = null, timer = null, pointer = { x: 0, y: 0 }, keyboard = false;

  root.querySelectorAll("button").forEach(button => {
    const key = button.dataset.tool ? `tools.${button.dataset.tool}` : `buttons.${button.id}`;
    const text = button.dataset.tool ? HELP.tools[button.dataset.tool] : HELP.buttons[button.id];
    if (text) {
      button.dataset.help = key;
      if (!button.getAttribute("aria-label")) button.setAttribute("aria-label", firstSentence(text));
    }
  });
  const sceneSelect = root.querySelector("#scene-list");
  if (sceneSelect) sceneSelect.dataset.help = "fields.scene";
  const previewDebug = root.querySelector("#preview-debug");
  if (previewDebug) previewDebug.dataset.help = "fields.previewDebug";
  root.querySelectorAll("#perspective-form [name]").forEach(field => field.dataset.help = `fields.${field.name}`);
  root.querySelectorAll("[data-scene-tab]").forEach(button=>{button.dataset.help="fields.sceneSettings";if(!button.getAttribute("aria-label"))button.setAttribute("aria-label",`${button.textContent.trim()} scene settings`)});
  root.querySelectorAll("#weather-form [name]").forEach(field=>{const name=field.name;field.dataset.help=name==="enabled"?"fields.weatherEnabled":name==="type"?"fields.weatherType":name==="mode"?"fields.weatherMode":name==="intensity"?"fields.weatherIntensity":name==="intensityVariation"?"fields.weatherVariation":name.startsWith("wind.")?"fields.weatherWind":name.startsWith("fade")?"fields.weatherFade":name.startsWith("random.")&&name!=="random.seedMode"&&name!=="random.fixedSeed"?"fields.weatherRandom":name.startsWith("random.")?"fields.weatherSeed":name.startsWith("layers.")?"fields.weatherLayers":name.startsWith("splashes.")?"fields.weatherSplashes":name==="quality"?"fields.weatherQuality":"fields.weatherState"});

  function textFor(target) {
    if (target.dataset.helpText) return target.dataset.helpText;
    const [group, ...parts] = (target.dataset.help || "").split(".");
    const key = parts.join("."), normalized = key.replace(/^interaction\.\d+\./, "interaction.");
    return HELP[group]?.[key] || HELP[group]?.[normalized] || "";
  }
  function described(target, enabled) {
    if (!target) return;
    if (enabled) {
      target.dataset.tooltipDescribed = "true";
      const ids = new Set((target.getAttribute("aria-describedby") || "").split(/\s+/).filter(Boolean));
      ids.add(tooltip.id); target.setAttribute("aria-describedby", [...ids].join(" "));
    } else if (target.dataset.tooltipDescribed) {
      const ids = (target.getAttribute("aria-describedby") || "").split(/\s+/).filter(id => id && id !== tooltip.id);
      ids.length ? target.setAttribute("aria-describedby", ids.join(" ")) : target.removeAttribute("aria-describedby");
      delete target.dataset.tooltipDescribed;
    }
  }
  function position() {
    if (!active || tooltip.hidden) return;
    const margin = 10, rect = tooltip.getBoundingClientRect();
    let x, y;
    if (keyboard) {
      const target = active.getBoundingClientRect();
      x = target.left + target.width / 2 - rect.width / 2; y = target.bottom + 9;
    } else { x = pointer.x + 14; y = pointer.y + 16; }
    x = Math.max(margin, Math.min(innerWidth - rect.width - margin, x));
    y = Math.max(margin, Math.min(innerHeight - rect.height - margin, y));
    tooltip.style.left = `${x}px`; tooltip.style.top = `${y}px`;
  }
  function show(target, viaKeyboard = false) {
    clearTimeout(timer); if (!target || !textFor(target)) return;
    if (active && active !== target) described(active, false);
    active = target; keyboard = viaKeyboard;
    timer = setTimeout(() => {
      if (!active) return;
      tooltip.textContent = textFor(active); tooltip.hidden = false; described(active, true);
      requestAnimationFrame(position);
    }, viaKeyboard ? 120 : 320);
  }
  function hide() {
    clearTimeout(timer); described(active, false); active = null; tooltip.hidden = true;
  }
  root.addEventListener("pointerover", event => {
    const target = event.target.closest?.("[data-help],[data-help-text]");
    if (target && target !== active) show(target, false);
  });
  root.addEventListener("pointerout", event => {
    if (active && !active.contains(event.relatedTarget)) hide();
  });
  root.addEventListener("pointermove", event => { pointer = { x: event.clientX, y: event.clientY }; if (!keyboard) position(); });
  root.addEventListener("focusin", event => {
    const target = event.target.closest?.("[data-help],[data-help-text]");
    if (target) show(target, true);
  });
  root.addEventListener("focusout", event => { if (active && active === event.target) hide(); });
  root.addEventListener("keydown", event => { if (event.key === "Escape") hide(); });
  addEventListener("resize", position);
  return { hide };
}
